import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Snackbar,
  Typography,
  Paper,
  Button,
} from "@mui/material";
import { NoSuggestionsTextField } from "../../../../components/textFields";
import {
  CustomDialog,
  CustomDialogActions,
  CustomDialogContent,
  CustomDialogTitle,
} from "../../../../components/customDialog";
import { CloseButton, SendButton } from "../../../../components/buttons";
import { makeRequest } from "../../../../assets/functions_helper";
import { useNavigate } from "react-router-dom";

// Paleta de colores
const COLORS = {
  PRIMARY: "#f73541",
  PRIMARY_HOVER: "#f7354254",
  BACKGROUND: "#ffffff",
  PAPER_BG: "#ffffff",
  TEXT_PRIMARY: "#1E1E1E",
  TEXT_SECONDARY: "#6A6A6A",
  BORDER: "#e0e0e0",
  SHADOW: "rgba(37, 150, 190, 0.1)",
};

function EditForm({
  currentName,
  setName,
  currentImageUrl,
  onImageChange,
  previewUrl,
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <NoSuggestionsTextField
        required
        margin="dense"
        id="name"
        label="Nombre de la Imagen"
        type="text"
        fullWidth
        value={currentName}
        onChange={(e) => setName(e.target.value)}
        size="small"
      />

      <Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>

        {/* Vista previa centrada — aparece ENCIMA del botón de selección */}
        {(previewUrl || currentImageUrl) && (
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              p: 2,
              border: `1px dashed ${COLORS.BORDER}`,
              borderRadius: "12px",
              backgroundColor: "#f9f9f9",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {previewUrl ? "Nueva imagen (vista previa)" : "Imagen actual"}
            </Typography>
            <img
              src={previewUrl || currentImageUrl}
              alt="Vista previa"
              style={{
                maxHeight: "180px",
                maxWidth: "100%",
                borderRadius: "10px",
                objectFit: "contain",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            />
          </Paper>
        )}

        {/* Botón de selección de imagen */}
        <input
          id="new_image"
          name="new_image"
          type="file"
          accept="image/*"
          onChange={onImageChange}
          style={{ display: "none" }}
        />
        <label htmlFor="new_image" style={{ width: "100%" }}>
          <Button
            component="span"
            variant="contained"
            sx={{
              backgroundColor: COLORS.PRIMARY,
              color: "white",
              width: "100%",
              py: 1.2,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { backgroundColor: COLORS.PRIMARY_HOVER },
            }}
          >
            {previewUrl ? "Cambiar imagen" : "Seleccionar nueva imagen"}
          </Button>
        </label>
        <Typography variant="caption" sx={{ color: COLORS.TEXT_SECONDARY, textAlign: "center" }}>
          Selecciona una imagen (PNG, JPG, JPEG) para reemplazar la actual. Si no seleccionas ninguna, se mantendrá la imagen existente.
        </Typography>
      </Box>
    </Box>
  );
}

function EditImage({
  open,
  handleClose,
  imageId,
  restaurantId,
  fetchImages,
  currentName,
  currentImageUrl,
}) {
  const navigate = useNavigate();
  const [name, setName] = useState(currentName || "");
  const [newImage, setNewImage] = useState(null); // Solo se llena si el usuario selecciona una nueva imagen
  const [previewUrl, setPreviewUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  // Actualizar el nombre cuando cambie currentName (por si se abre con otro ítem)
  useEffect(() => {
    setName(currentName || "");
  }, [currentName]);

  // Limpiar preview al desmontar
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setNewImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleModalClose = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setNewImage(null);
    setName(currentName || ""); // restaurar nombre original
    handleClose();
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      setSnackbar({ open: true, message: "El nombre no puede estar vacío", severity: "warning" });
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append("restaurant_id", restaurantId);
    formData.append("image_name", name.trim());

    // Solo agregar la nueva imagen si el usuario seleccionó una
    if (newImage) {
      formData.append("updated_image", newImage);
    }

    makeRequest(
      "PUT",
      `menu_manager/update_background_image/${imageId}`,
      formData,
      "multipart/form-data",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      null,
      null,
      () => navigate("/login")
    )
      .then(() => {
        setSnackbar({ open: true, message: "Imagen actualizada correctamente", severity: "success" });
        fetchImages();
        setTimeout(() => {
          handleModalClose();
        }, 1500);
      })
      .catch((error) => {
        console.error(error);
        setSnackbar({ open: true, message: "Error al actualizar la imagen", severity: "error" });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <CustomDialog open={open} onClose={handleModalClose} maxWidth="sm">
        <CustomDialogTitle>Editar Imagen</CustomDialogTitle>
        <CustomDialogContent dividers>
          <EditForm
            currentName={name}
            setName={setName}
            currentImageUrl={currentImageUrl}
            onImageChange={handleImageChange}
            previewUrl={previewUrl}
          />
        </CustomDialogContent>
        <CustomDialogActions>
          <CloseButton onClick={handleModalClose} disabled={submitting}>
            Cancelar
          </CloseButton>
          <SendButton onClick={handleSubmit} disabled={submitting}>
            {"Guardar Cambios"}
          </SendButton>
        </CustomDialogActions>
      </CustomDialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{
            fontWeight: 500
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default EditImage;